import { useEffect, useRef } from "react";
import { docsClient } from "@/lib/supabase";

/**
 * Background job processor hook
 * Runs in the background on any page to process queued jobs
 * Only one instance runs at a time across all browser tabs
 */
export function useJobProcessor() {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    // Don't run job processor during tests to prevent background timers
    if (import.meta.env.MODE === "test") return;

    // Only run if user is authenticated
    const checkAndProcess = async () => {
      // Prevent concurrent processing
      if (isProcessingRef.current) return;

      try {
        const {
          data: { session },
        } = await docsClient.auth.getSession();
        if (!session) return;

        isProcessingRef.current = true;

        // Call the process-jobs Edge Function
        const response = await fetch(
          `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/process-jobs`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.processed > 0) {
            console.log(`Job processor: Processed ${result.processed} jobs`);
          }
        }
      } catch (error) {
        // Silent fail - job processing is background work
        console.debug("Job processor error:", error);
      } finally {
        isProcessingRef.current = false;
      }
    };

    // Run immediately on mount
    checkAndProcess();

    // Then run every 30 seconds
    intervalRef.current = setInterval(checkAndProcess, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
}
